/* =====================================================================
 * UsageHeatmap — GitHub-style contribution grid.
 *
 * Form: a calendar heatmap is the right pick because the question is
 * "when was I active", and the answer is a magnitude read against a
 * date — not a comparison between categories.
 *
 * Colour: one hue, light→dark (sequential). Steps come from
 * `chartPalette`, which was validated rather than eyeballed. An empty
 * day uses a surface tint, never the ramp's lightest step — otherwise
 * "no activity" reads as "a little activity".
 *
 * Layout: columns are weeks, rows are weekdays — the standard reading
 * order for this form, so no legend is needed to explain the axes. The
 * grid is intrinsically wide, so on mobile it scrolls inside its own
 * card (scrolled to "today" on mount) rather than widening the page.
 * ===================================================================== */

import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type { DashboardHeatmap } from '@auw/api-client';

import { EMPTY_CELL, SEQUENTIAL, cellColor, type ChartMode } from './chartPalette';
import { ScrollX } from './dashboard.styles';
import { thinMonthMarks } from './dashboardMetrics';

const CELL = 11;
const GAP = 3;
const LABEL_W = 28;

/* The weekday column sits *outside* the scroller: an axis label that
 * scrolls away with the data stops being an axis. Only the grid moves. */
const Frame = styled.div`
  display: flex;
  gap: 6px;
  align-items: flex-start;
`;

const WeekdayColumn = styled.div`
  display: grid;
  grid-template-rows: repeat(7, ${CELL}px);
  gap: ${GAP}px;
  padding-top: 18px;
  width: ${LABEL_W}px;
  flex-shrink: 0;
`;

const WeekdayLabel = styled.span`
  font-size: 0.625rem;
  line-height: ${CELL}px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const GridArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MonthRow = styled.div`
  position: relative;
  height: 14px;
`;

const MonthLabel = styled.span<{ $left: number }>`
  position: absolute;
  left: ${({ $left }) => $left}px;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
`;

const Weeks = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-rows: repeat(7, ${CELL}px);
  gap: ${GAP}px;
`;

/* A 1px surface ring keeps adjacent cells from fusing into a block —
 * the 2px separation rule for touching marks. */
const Cell = styled.div<{ $color: string }>`
  width: ${CELL}px;
  height: ${CELL}px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
  box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.bgSecondary};
  transition: transform 0.08s ease;
  cursor: default;

  &:hover {
    transform: scale(1.25);
  }
`;

const Legend = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 10px;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const LegendSwatch = styled.span<{ $color: string }>`
  width: ${CELL}px;
  height: ${CELL}px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
  box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.bgSecondary};
`;

export interface UsageHeatmapProps {
  data: DashboardHeatmap;
  mode: ChartMode;
}

interface PositionedCell {
  date: string;
  count: number;
  /** 0 = Sunday, matching the grid's row order. */
  weekday: number;
  week: number;
}

export const UsageHeatmap: React.FC<UsageHeatmapProps> = ({ data, mode }) => {
  const { t, i18n } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);

  const { cells, weekCount, monthMarks } = useMemo(() => {
    const positioned: PositionedCell[] = [];
    const marks: Array<{ week: number; label: string }> = [];
    let lastMonth = -1;

    // Pad the first week so the grid starts on the correct weekday —
    // without this every column after a partial first week is shifted.
    const first = new Date(`${data.cells[0]?.date ?? data.start}T00:00:00Z`);
    const leading = first.getUTCDay();

    data.cells.forEach((cell, index) => {
      const absolute = index + leading;
      const week = Math.floor(absolute / 7);
      const weekday = absolute % 7;
      positioned.push({ ...cell, week, weekday });

      const month = new Date(`${cell.date}T00:00:00Z`).getUTCMonth();
      if (month !== lastMonth && weekday <= 3) {
        // Label at the top of the column where the month begins; the
        // weekday guard stops a label landing on a stub column.
        marks.push({
          week,
          label: new Intl.DateTimeFormat(i18n.language, {
            month: 'short',
            timeZone: 'UTC',
          }).format(new Date(`${cell.date}T00:00:00Z`)),
        });
        lastMonth = month;
      }
    });

    const totalWeeks = positioned.length
      ? positioned[positioned.length - 1].week + 1
      : 0;
    return { cells: positioned, weekCount: totalWeeks, monthMarks: thinMonthMarks(marks) };
  }, [data, i18n.language]);

  // Land on the most recent weeks — the year grid is wider than a phone
  // and the interesting end is the right one.
  useEffect(() => {
    const node = scrollRef.current;
    if (node) node.scrollLeft = node.scrollWidth;
  }, [weekCount]);

  const weekdayLabels = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(i18n.language, {
      weekday: 'short',
      timeZone: 'UTC',
    });
    // 2026-07-05 is a Sunday — anchors the row order to the grid.
    return [0, 1, 2, 3, 4, 5, 6].map((offset) =>
      formatter.format(new Date(Date.UTC(2026, 6, 5 + offset))),
    );
  }, [i18n.language]);

  const byPosition = useMemo(() => {
    const map = new Map<string, PositionedCell>();
    cells.forEach((cell) => map.set(`${cell.week}:${cell.weekday}`, cell));
    return map;
  }, [cells]);

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC',
      }),
    [i18n.language],
  );

  return (
    <>
      <Frame>
        <WeekdayColumn aria-hidden>
          {weekdayLabels.map((label, index) => (
            // Mon / Wed / Fri only — labelling all seven crowds the axis.
            <WeekdayLabel key={label}>
              {index % 2 === 1 ? label : ''}
            </WeekdayLabel>
          ))}
        </WeekdayColumn>

        <ScrollX ref={scrollRef}>
          <GridArea>
            <MonthRow aria-hidden>
              {monthMarks.map((mark) => (
                <MonthLabel key={`${mark.week}-${mark.label}`} $left={mark.week * (CELL + GAP)}>
                  {mark.label}
                </MonthLabel>
              ))}
            </MonthRow>

            <Weeks role="grid" aria-label={t('common:dashboard.heatmapLabel')}>
              {Array.from({ length: weekCount * 7 }, (_, index) => {
                const week = Math.floor(index / 7);
                const weekday = index % 7;
                const cell = byPosition.get(`${week}:${weekday}`);
                if (!cell) {
                  return <div key={index} style={{ width: CELL, height: CELL }} />;
                }
                return (
                  <Cell
                    key={cell.date}
                    role="gridcell"
                    $color={cellColor(cell.count, data.peak, mode)}
                    title={`${dateFormatter.format(
                      new Date(`${cell.date}T00:00:00Z`),
                    )} · ${t('common:dashboard.runsCount', { count: cell.count })}`}
                    aria-label={`${cell.date}: ${cell.count}`}
                  />
                );
              })}
            </Weeks>
          </GridArea>
        </ScrollX>
      </Frame>

      <Legend>
        <span>{t('common:dashboard.legendLess')}</span>
        <LegendSwatch $color={EMPTY_CELL[mode]} />
        {SEQUENTIAL[mode].map((color) => (
          <LegendSwatch key={color} $color={color} />
        ))}
        <span>{t('common:dashboard.legendMore')}</span>
      </Legend>
    </>
  );
};
